import { StudyModule } from '../types/database';
import OpenAI from 'openai';

// Initialize the OpenAI client with OpenRouter API key
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENROUTER_API_KEY || import.meta.env.VITE_DEEPSEEK_API_KEY,
  baseURL: import.meta.env.VITE_OPENROUTER_BASE_URL || 'https://openrouter.ai/api/v1',
  defaultHeaders: {
    "HTTP-Referer": "https://masterymint.app", // Optional. Site URL for rankings on openrouter.ai.
    "X-Title": "MasteryMint", // Optional. Site title for rankings on openrouter.ai.
  },
  dangerouslyAllowBrowser: true, // Allow browser usage (API key will be exposed in client)
});

// Define the structure for AI-generated content
export interface AIGeneratedContent {
  summary: {
    simpleExplanation: string;
    keyPoints: Array<{
      title: string;
      description: string;
      icon: string;
    }>;
  };
  diagrams: {
    mindMap: string;
    linearFlow: string;
    components: Array<{
      title: string;
      description: string;
      icon: string;
    }>;
  };
  flashcards: Array<{
    front: string;
    back: string;
  }>;
  quiz: {
    questions: Array<{
      id: string;
      question: string;
      options: string[];
      correctAnswer: number;
    }>;
  };
  audio: {
    transcript: string;
    duration: number; // in seconds
  };
}

// Custom error class for AI generation failures
export class AIGenerationError extends Error {
  constructor(message: string, public originalError?: Error) {
    super(message);
    this.name = 'AIGenerationError';
  }
}

// Cache for AI-generated content to avoid duplicate API calls
const contentCache = new Map<string, AIGeneratedContent>();

// Generate learning material using OpenRouter AI
export const generateLearningMaterial = async (
  query: string,
  fileContent?: string
): Promise<AIGeneratedContent> => {
  // Create a cache key based on the query and file content
  const cacheKey = `${query}${fileContent ? `-${fileContent.substring(0, 100)}` : ''}`;
  
  // Check if we have cached content for this query
  if (contentCache.has(cacheKey)) {
    return contentCache.get(cacheKey)!;
  }
  
  try {
    // Check if API key is configured
    if (!import.meta.env.VITE_OPENROUTER_API_KEY && !import.meta.env.VITE_DEEPSEEK_API_KEY) {
      console.warn('OpenRouter API key not configured, using fallback content');
      const topic = extractTopic(query, fileContent);
      const content = generateContentForTopic(topic);
      contentCache.set(cacheKey, content);
      return content;
    }

    // Extract main topic from query
    const topic = extractTopic(query, fileContent);
    
    // Prepare the prompt for OpenRouter AI
    const prompt = createPrompt(topic, query, fileContent);
    
    console.log('Sending prompt to OpenRouter AI:', prompt.substring(0, 200) + '...');
    
    // Call OpenRouter AI API with timeout and error handling
    const completion = await Promise.race([
      openai.chat.completions.create({
        model: "deepseek/deepseek-r1-0528-qwen3-8b:free", // Updated model
        messages: [
          {
            role: 'system',
            content: 'You are an expert educational content creator specializing in creating comprehensive learning materials. Your task is to generate well-structured, accurate, and engaging educational content based on the user\'s query or uploaded document.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 4000,
      }),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 90000) // 90 seconds timeout
      )
    ]) as OpenAI.Chat.Completions.ChatCompletion;
    
    // Parse the response
    const aiResponse = completion.choices[0].message.content;
    console.log('Received response from OpenRouter AI:', aiResponse?.substring(0, 200) + '...');
    
    if (!aiResponse) {
      console.warn('Empty AI response, using fallback content');
      const content = generateContentForTopic(topic);
      contentCache.set(cacheKey, content);
      return content;
    }
    
    // Try to parse the response as JSON with improved extraction logic
    let content: AIGeneratedContent;
    try {
      // First, try to parse the response directly as JSON
      content = JSON.parse(aiResponse);
      console.log('Successfully parsed AI response as JSON');
    } catch (error) {
      console.warn('Failed to parse AI response as JSON, attempting to extract JSON');
      
      // Improved JSON extraction logic
      const extractedJson = extractJsonFromResponse(aiResponse);
      
      if (extractedJson) {
        try {
          content = JSON.parse(extractedJson);
          console.log('Successfully extracted and parsed JSON from AI response');
        } catch (extractError) {
          console.warn('Failed to parse extracted JSON, using fallback content');
          content = generateContentForTopic(topic);
        }
      } else {
        console.warn('No valid JSON found in AI response, using fallback content');
        content = generateContentForTopic(topic);
      }
    }
    
    // Ensure the content has all required fields
    content = validateAndFixContent(content, topic);
    
    // Cache the content for future use
    contentCache.set(cacheKey, content);
    
    // Return the content
    return content;
  } catch (error) {
    console.error('Error generating learning material with OpenRouter AI:', error);
    
    // Provide more specific error information
    let errorMessage = 'Unknown error occurred';
    if (error instanceof Error) {
      if (error.message.includes('fetch')) {
        errorMessage = 'Network connection failed. Please check your internet connection and try again.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out. The AI service may be temporarily unavailable.';
      } else if (error.message.includes('401') || error.message.includes('403')) {
        errorMessage = 'API authentication failed. Please check your API key configuration.';
      } else if (error.message.includes('429')) {
        errorMessage = 'Rate limit exceeded. Please wait a moment and try again.';
      } else {
        errorMessage = error.message;
      }
    }
    
    console.warn(`AI generation failed: ${errorMessage}. Using fallback content.`);
    
    // Fallback to predefined content if AI generation fails
    const topic = extractTopic(query, fileContent);
    const content = generateContentForTopic(topic);
    contentCache.set(cacheKey, content);
    return content;
  }
};

// Improved JSON extraction function
const extractJsonFromResponse = (response: string): string | null => {
  // Strategy 1: Look for JSON within markdown code blocks
  const jsonCodeBlockPatterns = [
    /```json\s*\n([\s\S]*?)\n```/i,
    /```\s*\n([\s\S]*?)\n```/,
    /`([\s\S]*?)`/
  ];
  
  for (const pattern of jsonCodeBlockPatterns) {
    const match = response.match(pattern);
    if (match && match[1]) {
      const candidate = match[1].trim();
      // Try to parse directly instead of using custom validation
      try {
        JSON.parse(candidate);
        return candidate;
      } catch {
        continue;
      }
    }
  }
  
  // Strategy 2: Find content between first { and last }
  const firstBrace = response.indexOf('{');
  const lastBrace = response.lastIndexOf('}');
  
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    const candidate = response.substring(firstBrace, lastBrace + 1);
    try {
      JSON.parse(candidate);
      return candidate;
    } catch {
      // Continue to next strategy
    }
  }
  
  // Strategy 3: Look for JSON-like patterns with balanced braces
  const jsonPattern = /{[\s\S]*}/;
  const match = response.match(jsonPattern);
  if (match) {
    const candidate = match[0];
    try {
      JSON.parse(candidate);
      return candidate;
    } catch {
      // Continue to next strategy
    }
  }
  
  return null;
};

// Create a detailed prompt for the AI
const createPrompt = (topic: string, query: string, fileContent?: string): string => {
  let prompt = `Create comprehensive learning materials for the topic: "${topic}".`;
  
  if (fileContent) {
    prompt += `\n\nI've uploaded a document with the following content:\n\n${fileContent.substring(0, 2000)}${fileContent.length > 2000 ? '...' : ''}`;
  } else {
    prompt += `\n\nPlease create materials based on this query: "${query}"`;
  }
  
  prompt += `\n\nPlease generate a complete learning module with the following components:

1. Summary:
   - A simple explanation of the topic (2-3 paragraphs)
   - 4 key points with titles, descriptions, and suggested icon names (use simple icon names like "leaf", "sun", "zap", "activity", etc.)

2. Diagrams:
   - A mind map of the topic in Mermaid 'graph TD' syntax. Use simple node IDs (A, B, C, etc.) and avoid special characters in labels.
   - A linear flow diagram of the topic in Mermaid 'graph LR' syntax. Use simple node IDs and avoid special characters in labels.
   - 3 component descriptions with titles, descriptions, and icon names

3. Flashcards:
   - 5-10 flashcards with front (question) and back (answer) content
   - Make the questions challenging but clear
   - Provide comprehensive answers that enhance understanding
   - Cover different aspects and difficulty levels of the topic

4. Quiz:
   - 5-10 multiple-choice questions with 4 options each
   - Indicate the index of the correct answer (0-3)
   - Vary the difficulty to test different levels of understanding
   - Each question should have a unique ID (e.g., "q1", "q2", etc.)

5. Audio Lesson:
   - A transcript for an audio lesson (about 500-800 words)
   - The transcript should flow naturally as spoken content with clear transitions
   - Include an introduction, main content sections, and a conclusion
   - Estimate a duration in seconds (typically 8-12 minutes)

IMPORTANT MERMAID SYNTAX RULES:
- Use only simple alphanumeric node IDs (A, B, C, D1, D2, etc.)
- Keep node labels short and simple
- Avoid parentheses, commas, and special characters in node labels
- Use underscores instead of spaces in labels if needed
- Example: A[Main_Topic] --> B[Subtopic_1]

Focus on accuracy, clarity, and educational value. The content should be engaging and suitable for a student trying to master this topic. Include practical examples and real-world applications where appropriate.

Format the response as a valid JSON object matching this structure:
{
  "summary": {
    "simpleExplanation": "string",
    "keyPoints": [
      {
        "title": "string",
        "description": "string",
        "icon": "string"
      }
    ]
  },
  "diagrams": {
    "mindMap": "string (Mermaid 'graph TD' syntax)",
    "linearFlow": "string (Mermaid 'graph LR' syntax)",
    "components": [
      {
        "title": "string",
        "description": "string",
        "icon": "string"
      }
    ]
  },
  "flashcards": [
    {
      "front": "string",
      "back": "string"
    }
  ],
  "quiz": {
    "questions": [
      {
        "id": "string",
        "question": "string",
        "options": ["string", "string", "string", "string"],
        "correctAnswer": 0
      }
    ]
  },
  "audio": {
    "transcript": "string",
    "duration": 600
  }
}

IMPORTANT: Your response must be a valid JSON object that can be parsed with JSON.parse(). Do not include any text before or after the JSON object.`;

  return prompt;
};

// Helper function to extract main topic from query or file content
const extractTopic = (query: string, fileContent?: string): string => {
  // Simple extraction logic - in a real app, this would be more sophisticated
  const defaultTopics = ['Photosynthesis', 'Cell Biology', 'Chemical Bonds', 'Genetics'];
  
  // Check if query contains specific topics
  for (const topic of defaultTopics) {
    if (query.toLowerCase().includes(topic.toLowerCase())) {
      return topic;
    }
  }
  
  // Check if file content contains specific topics
  if (fileContent) {
    for (const topic of defaultTopics) {
      if (fileContent.toLowerCase().includes(topic.toLowerCase())) {
        return topic;
      }
    }
  }
  
  // Extract topic from query using simple heuristics
  if (query.includes('about') || query.includes('explain')) {
    const match = query.match(/about\s+([^.?!]+)|explain\s+([^.?!]+)/i);
    if (match) {
      const extracted = (match[1] || match[2]).trim();
      // Capitalize first letter
      return extracted.charAt(0).toUpperCase() + extracted.slice(1);
    }
  }
  
  // If query contains "uploaded", extract the document type
  if (query.toLowerCase().includes('uploaded')) {
    if (query.toLowerCase().includes('pdf')) return 'Document Content';
    if (query.toLowerCase().includes('docx')) return 'Document Content';
    if (query.toLowerCase().includes('txt')) return 'Document Content';
  }
  
  // Extract the first few words if the query is long enough
  if (query.length > 10) {
    const words = query.split(' ');
    if (words.length >= 3) {
      const shortTopic = words.slice(0, 3).join(' ');
      return shortTopic.charAt(0).toUpperCase() + shortTopic.slice(1);
    }
  }
  
  // Default to a generic topic if we can't extract anything specific
  return query.length > 20 ? 'Custom Topic' : defaultTopics[0];
};

// Function to validate and sanitize Mermaid diagram syntax
const validateMermaidSyntax = (diagram: string, expectedPrefix: string): string => {
  if (!diagram || typeof diagram !== 'string') {
    return '';
  }
  
  // Check if the diagram starts with the expected prefix
  if (!diagram.trim().startsWith(expectedPrefix)) {
    return '';
  }
  
  // Basic validation: check for balanced brackets and valid syntax
  try {
    // Remove the prefix to check the content
    const content = diagram.replace(expectedPrefix, '').trim();
    
    // Check for basic Mermaid syntax patterns
    const hasValidNodes = /[A-Z]\d*\[/.test(content);
    const hasValidConnections = /-->/.test(content);
    
    if (!hasValidNodes || !hasValidConnections) {
      return '';
    }
    
    // Sanitize the diagram by removing problematic characters
    let sanitized = diagram
      // Replace problematic characters in node labels
      .replace(/\[([^\]]*)\(/g, (match, label) => `[${label.replace(/[(),]/g, '_')}`)
      .replace(/\[([^\]]*),([^\]]*)\]/g, (match, p1, p2) => `[${p1}_${p2}]`)
      // Ensure proper semicolon endings
      .replace(/([^;])\s*$/, '$1;');
    
    return sanitized;
  } catch (error) {
    console.warn('Mermaid syntax validation failed:', error);
    return '';
  }
};

// Validate and fix content to ensure all required fields are present
const validateAndFixContent = (content: any, topic: string): AIGeneratedContent => {
  const fallbackContent = generateContentForTopic(topic);
  
  // Check if content is null or undefined
  if (!content) return fallbackContent;
  
  // Validate and sanitize Mermaid diagrams
  let mindMap = '';
  let linearFlow = '';
  
  if (content.diagrams?.mindMap) {
    mindMap = validateMermaidSyntax(content.diagrams.mindMap, 'graph TD');
  }
  
  if (content.diagrams?.linearFlow) {
    linearFlow = validateMermaidSyntax(content.diagrams.linearFlow, 'graph LR');
  }
  
  // Use fallback diagrams if validation fails
  if (!mindMap) {
    console.warn('Invalid mindMap diagram, using fallback');
    mindMap = fallbackContent.diagrams.mindMap;
  }
  
  if (!linearFlow) {
    console.warn('Invalid linearFlow diagram, using fallback');
    linearFlow = fallbackContent.diagrams.linearFlow;
  }
  
  // Create a new object with all required fields
  const validatedContent: AIGeneratedContent = {
    summary: {
      simpleExplanation: content.summary?.simpleExplanation || fallbackContent.summary.simpleExplanation,
      keyPoints: Array.isArray(content.summary?.keyPoints) && content.summary.keyPoints.length > 0
        ? content.summary.keyPoints
        : fallbackContent.summary.keyPoints
    },
    diagrams: {
      mindMap,
      linearFlow,
      components: Array.isArray(content.diagrams?.components) && content.diagrams.components.length > 0
        ? content.diagrams.components
        : fallbackContent.diagrams.components
    },
    flashcards: Array.isArray(content.flashcards) && content.flashcards.length > 0
      ? content.flashcards
      : fallbackContent.flashcards,
    quiz: {
      questions: Array.isArray(content.quiz?.questions) && content.quiz.questions.length > 0
        ? content.quiz.questions
        : fallbackContent.quiz.questions
    },
    audio: {
      transcript: content.audio?.transcript || fallbackContent.audio.transcript,
      duration: content.audio?.duration || fallbackContent.audio.duration
    }
  };
  
  return validatedContent;
};

// Generate mock content for a topic (fallback if AI generation fails)
const generateContentForTopic = (topic: string): AIGeneratedContent => {
  // Predefined content for common topics
  const contentLibrary: Record<string, AIGeneratedContent> = {
    'Photosynthesis': {
      summary: {
        simpleExplanation: "Photosynthesis is how plants make their own food using sunlight. Just like we use a kitchen to turn ingredients into meals, plants use their leaves as solar-powered kitchens. They take in sunlight through their leaves, water from their roots, and carbon dioxide from the air. Using these ingredients, they create glucose (sugar) for food and release oxygen as a byproduct - which is great news for us because we need oxygen to breathe!",
        keyPoints: [
          {
            title: "Sunlight Energy",
            description: "Plants capture sunlight energy through their leaves",
            icon: "sun"
          },
          {
            title: "Water Absorption",
            description: "Water is absorbed through the roots and travels up to leaves",
            icon: "droplet"
          },
          {
            title: "Carbon Dioxide",
            description: "Carbon dioxide from air combines with water in leaves",
            icon: "wind"
          },
          {
            title: "Glucose Production",
            description: "Sunlight energy powers the creation of glucose (plant food)",
            icon: "zap"
          }
        ]
      },
      diagrams: {
        mindMap: "graph TD;\n  A[Photosynthesis] --> B[Light_Reactions];\n  A --> C[Calvin_Cycle];\n  B --> D[Chlorophyll];\n  B --> E[Water_Splitting];\n  B --> F[ATP_NADPH_Production];\n  C --> G[Carbon_Fixation];\n  C --> H[Glucose_Synthesis];\n  A --> I[Environmental_Factors];\n  I --> J[Light_Intensity];\n  I --> K[CO2_Concentration];\n  I --> L[Temperature];",
        linearFlow: "graph LR;\n  A[Sunlight] --> B[Chlorophyll];\n  C[Water] --> D[Photolysis];\n  D --> E[Oxygen_Release];\n  B --> F[ATP_NADPH];\n  G[Carbon_Dioxide] --> H[Calvin_Cycle];\n  F --> H;\n  H --> I[Glucose];",
        components: [
          {
            title: "Primary Components",
            description: "Light, water, and carbon dioxide are the main inputs",
            icon: "sun"
          },
          {
            title: "Process Flow",
            description: "Light reactions followed by Calvin cycle",
            icon: "refresh-cw"
          },
          {
            title: "Key Interactions",
            description: "Energy transfer and molecule transformations",
            icon: "git-branch"
          }
        ]
      },
      flashcards: [
        {
          front: "What is the primary energy source for photosynthesis?",
          back: "Sunlight is the primary energy source for photosynthesis. Plants capture light energy through chlorophyll in their leaves and convert it into chemical energy."
        },
        {
          front: "Where does photosynthesis take place in plant cells?",
          back: "Photosynthesis occurs in the chloroplasts, specifically in structures called thylakoids where chlorophyll is located."
        },
        {
          front: "What are the main products of photosynthesis?",
          back: "The main products are glucose (C₆H₁₂O₆) and oxygen (O₂). Glucose serves as food for the plant, while oxygen is released into the atmosphere."
        },
        {
          front: "What raw materials does photosynthesis require?",
          back: "Photosynthesis requires carbon dioxide (CO₂) from the air, water (H₂O) from the roots, and sunlight energy captured by chlorophyll."
        },
        {
          front: "What is the chemical equation for photosynthesis?",
          back: "6CO₂ + 6H₂O + light energy → C₆H₁₂O₆ + 6O₂. This shows how carbon dioxide and water are converted into glucose and oxygen."
        }
      ],
      quiz: {
        questions: [
          {
            id: "q1",
            question: "Which component is essential for plants to begin the process of photosynthesis?",
            options: ["Sunlight", "Soil nutrients", "Root system", "Flower petals"],
            correctAnswer: 0
          },
          {
            id: "q2",
            question: "Where does photosynthesis primarily occur in plants?",
            options: ["Roots", "Leaves", "Stems", "Flowers"],
            correctAnswer: 1
          },
          {
            id: "q3",
            question: "What gas is released as a byproduct of photosynthesis?",
            options: ["Carbon dioxide", "Nitrogen", "Oxygen", "Hydrogen"],
            correctAnswer: 2
          },
          {
            id: "q4",
            question: "Which molecule stores the energy produced in photosynthesis?",
            options: ["Water", "Carbon dioxide", "Oxygen", "Glucose"],
            correctAnswer: 3
          },
          {
            id: "q5",
            question: "What pigment is primarily responsible for capturing light energy?",
            options: ["Chlorophyll", "Carotene", "Anthocyanin", "Xanthophyll"],
            correctAnswer: 0
          }
        ]
      },
      audio: {
        transcript: "Welcome to this lesson on photosynthesis. In this comprehensive overview, we'll explore how plants convert sunlight into energy through this remarkable process.\n\nPhotosynthesis is essentially nature's way of capturing and storing solar energy. Plants act like living solar panels, using specialized structures called chloroplasts to absorb light energy.\n\nThe process begins when chlorophyll, the green pigment in plant leaves, absorbs photons from sunlight. This energy is then used to split water molecules, releasing oxygen as a byproduct.\n\nMeanwhile, carbon dioxide from the atmosphere is combined with the hydrogen from water to create glucose, which serves as the plant's primary energy source.\n\nThis process not only feeds the plant but also produces the oxygen we breathe, making photosynthesis one of the most important biological processes on Earth.",
        duration: 600 // 10 minutes
      }
    },
    'Custom Topic': {
      summary: {
        simpleExplanation: "This is a custom topic based on your query. The content has been generated to provide a comprehensive overview of the subject matter, covering key concepts, principles, and applications.",
        keyPoints: [
          {
            title: "Core Concepts",
            description: "Fundamental principles and key ideas",
            icon: "zap"
          },
          {
            title: "Applications",
            description: "Real-world uses and practical examples",
            icon: "target"
          },
          {
            title: "Processes",
            description: "Step-by-step mechanisms and procedures",
            icon: "list"
          },
          {
            title: "Connections",
            description: "Relationships to other topics and broader context",
            icon: "git-branch"
          }
        ]
      },
      diagrams: {
        mindMap: "graph TD;\n  A[Main_Topic] --> B[Core_Concepts];\n  A --> C[Applications];\n  A --> D[Methodologies];\n  A --> E[Related_Fields];\n  B --> F[Principle_1];\n  B --> G[Principle_2];\n  C --> H[Use_Case_1];\n  C --> I[Use_Case_2];\n  D --> J[Method_1];\n  D --> K[Method_2];\n  E --> L[Field_1];\n  E --> M[Field_2];",
        linearFlow: "graph LR;\n  A[Basic_Principles] --> B[Concept_Development];\n  B --> C[Practical_Implementation];\n  C --> D[Testing_Validation];\n  D --> E[Refinement];\n  E --> F[Advanced_Applications];",
        components: [
          {
            title: "Main Elements",
            description: "Primary components and their functions",
            icon: "layers"
          },
          {
            title: "Relationships",
            description: "How different elements interact and connect",
            icon: "git-merge"
          },
          {
            title: "Structure",
            description: "Organization and hierarchy of components",
            icon: "grid"
          }
        ]
      },
      flashcards: [
        {
          front: "What are the key principles of this topic?",
          back: "The key principles include: 1) Core foundational concepts that form the basis of understanding, 2) Practical applications in various contexts, 3) Systematic processes that demonstrate how components interact, and 4) Interconnections with related fields and broader implications."
        },
        {
          front: "How is this topic applied in real-world scenarios?",
          back: "This topic has numerous real-world applications, including: problem-solving in professional settings, development of innovative solutions, analysis of complex systems, and enhancement of existing processes or products."
        },
        {
          front: "What are the historical developments in this field?",
          back: "The field has evolved through several key phases: initial discovery and conceptualization, refinement of theories through experimentation, technological advancements enabling deeper understanding, and modern applications that continue to expand the boundaries of knowledge."
        },
        {
          front: "What are common misconceptions about this topic?",
          back: "Common misconceptions include: oversimplification of complex processes, confusion between correlation and causation, misunderstanding of fundamental principles, and outdated information that has been revised through recent research."
        },
        {
          front: "How does this topic connect to other fields of study?",
          back: "This topic connects to other fields through shared methodologies, overlapping concepts, interdisciplinary applications, and common theoretical frameworks that span multiple domains of knowledge."
        }
      ],
      quiz: {
        questions: [
          {
            id: "q1",
            question: "Which of the following best describes a core principle of this topic?",
            options: ["Isolated concepts with no practical application", "Theoretical frameworks with real-world implications", "Purely abstract ideas without structure", "Temporary hypotheses awaiting disproval"],
            correctAnswer: 1
          },
          {
            id: "q2",
            question: "What is a key characteristic of effective application of this knowledge?",
            options: ["Rigid adherence to outdated methodologies", "Flexible adaptation to different contexts", "Avoiding collaboration with other disciplines", "Rejecting empirical evidence"],
            correctAnswer: 1
          },
          {
            id: "q3",
            question: "How do experts in this field typically approach complex problems?",
            options: ["By applying a single solution to all scenarios", "By breaking problems into manageable components", "By avoiding theoretical frameworks", "By working in isolation from other experts"],
            correctAnswer: 1
          },
          {
            id: "q4",
            question: "What role does critical thinking play in this field?",
            options: ["It is discouraged in favor of memorization", "It is essential for evaluating evidence and drawing conclusions", "It is only relevant for beginners", "It has been replaced by algorithmic processes"],
            correctAnswer: 1
          },
          {
            id: "q5",
            question: "How has technology impacted this field in recent years?",
            options: ["It has made the field obsolete", "It has had no significant impact", "It has enhanced research capabilities and applications", "It has completely replaced traditional methodologies"],
            correctAnswer: 2
          }
        ]
      },
      audio: {
        transcript: "Welcome to this comprehensive overview of the topic you've requested. In this audio lesson, we'll explore the fundamental concepts, practical applications, and broader implications of this subject area.\n\nLet's begin by examining the core principles that form the foundation of understanding in this field. These principles have been developed and refined through years of research, experimentation, and practical application.\n\nOne of the most fascinating aspects of this topic is how it connects to other areas of study. These interdisciplinary connections highlight the integrated nature of knowledge and how concepts from different fields can inform and enhance each other.\n\nAs we delve deeper, we'll explore the practical applications of these concepts in real-world scenarios. Understanding how theoretical knowledge translates into practical solutions is essential for mastering this subject.\n\nWe'll also address common misconceptions and challenges that learners often encounter. By clarifying these points of confusion, you'll develop a more accurate and nuanced understanding of the material.\n\nThroughout this lesson, we'll emphasize critical thinking and analytical approaches that will help you not just memorize information, but truly comprehend and apply it in various contexts.\n\nBy the end of this audio lesson, you should have a solid grasp of the fundamental concepts, their applications, and how they fit into the broader landscape of knowledge in this field.",
        duration: 540 // 9 minutes
      }
    }
  };
  
  // Return content for the specified topic, or default to Custom Topic if not found
  return contentLibrary[topic] || contentLibrary['Custom Topic'];
};